const UNSUPPORTED_FIELD_KEYS = new Set([
  'title',
  'sku',
  'barcode',
  'handle',
  'availableOptions',
  'options',
  'listingStatus',
  'keywords',
  'weightValue',
  'weightUnit',
]);

const PRODUCT_FIELD_KEYS = new Set([
  'description',
  'descriptionHtml',
  'vendor',
  'productType',
  'tags',
  'seoTitle',
  'seoDescription',
  'category',
  'status',
  'templateSuffix',
]);

const VARIANT_FIELD_KEYS = new Set([
  'taxCode',
  'availableForSale',
  'inventoryPolicy',
]);

const SHARED_FIELD_KEYS = new Set([
  'price',
  'compareAtPrice',
  'cost',
  'unitCost',
  'taxable',
  'tracked',
  'requiresShipping',
  'weight',
  'countryCodeOfOrigin',
  'harmonizedSystemCode',
]);

const NUMBER_FIELD_KEYS = new Set(['price', 'compareAtPrice', 'cost', 'unitCost']);

const CLEAR_VALUES = {
  description: '',
  descriptionHtml: '',
  vendor: '',
  productType: '',
  tags: [],
  seoTitle: '',
  seoDescription: '',
  category: null,
  templateSuffix: '',
  taxCode: '',
  countryCodeOfOrigin: '',
  harmonizedSystemCode: '',
  price: 0,
  compareAtPrice: 0,
  cost: 0,
  unitCost: 0,
  weight: { value: 0, unit: 'grams' },
};

export const getAggregatedSavingKey = ({ fieldKey, target, isMeta } = {}) => {
  const resolvedTarget = (!isMeta && SHARED_FIELD_KEYS.has(fieldKey))
    ? 'product'
    : (target || 'product');

  return `${resolvedTarget}:${isMeta ? 'meta.' : ''}${fieldKey}`;
};

export const resolveBulkMasterTargets = (fieldKey, { isMeta, target } = {}) => {
  if (isMeta) {
    return target === 'variant' ? ['variant'] : ['product'];
  }

  if (!fieldKey || UNSUPPORTED_FIELD_KEYS.has(fieldKey)) return [];

  if (PRODUCT_FIELD_KEYS.has(fieldKey)) return ['product'];
  if (VARIANT_FIELD_KEYS.has(fieldKey)) return ['variant'];

  if (SHARED_FIELD_KEYS.has(fieldKey)) {
    // Explicit bulk value applies to every selected product and variant.
    return ['product', 'variant'];
  }

  return [];
};

const coerceNumber = (value) => {
  if (value === '' || value == null) return 0;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildCategoryPayload = (value) => {
  if (value == null) return { category: null };
  if (typeof value !== 'object' || !value._id) return null;

  return {
    category: {
      _id: value._id,
      name: value.name,
      fullName: value.fullName ?? null,
    },
  };
};

const buildWeightPayload = (value) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const numericValue = coerceNumber(value);
    if (numericValue == null) return null;
    return { weight: { value: numericValue, unit: 'grams' } };
  }

  if (!value || typeof value !== 'object') {
    return { weight: { value: 0, unit: 'grams' } };
  }

  const numericValue = coerceNumber(value.value);
  if (numericValue == null) return null;

  return {
    weight: {
      unit: value.unit || 'grams',
      value: numericValue,
    },
  };
};

export const buildBulkMasterUpdateData = (fieldKey, value, { isMeta, isClear } = {}) => {
  if (isMeta) {
    return { meta: { [fieldKey]: isClear ? '' : value } };
  }

  if (!fieldKey || UNSUPPORTED_FIELD_KEYS.has(fieldKey)) return null;

  const isKnownField = PRODUCT_FIELD_KEYS.has(fieldKey)
    || VARIANT_FIELD_KEYS.has(fieldKey)
    || SHARED_FIELD_KEYS.has(fieldKey);

  if (!isKnownField) return null;

  if (isClear && !(fieldKey in CLEAR_VALUES)) return null;

  const resolvedValue = isClear ? CLEAR_VALUES[fieldKey] : value;

  if (fieldKey === 'category') return buildCategoryPayload(resolvedValue);
  if (fieldKey === 'weight') return buildWeightPayload(resolvedValue);

  if (NUMBER_FIELD_KEYS.has(fieldKey)) {
    const numericValue = coerceNumber(resolvedValue);
    if (numericValue == null) return null;
    return { [fieldKey]: numericValue };
  }

  return { [fieldKey]: resolvedValue };
};

export const splitMasterSelectionItems = (items = []) => {
  const productIds = [];
  const variantIds = [];

  items.forEach((item) => {
    if (item?.type === 'master' && item.id) productIds.push(item.id);
    if (item?.type === 'masterVariant' && item.id) variantIds.push(item.id);
  });

  return { productIds, variantIds };
};

export const splitListingSelectionItems = (items = []) => {
  const listingIds = [];
  const variantIds = [];

  items.forEach((item) => {
    if (item?.type === 'listing' && item.id) listingIds.push(item.id);
    if (item?.type === 'listingVariant' && item.id) variantIds.push(item.id);
  });

  return { listingIds, variantIds };
};

const entityTypeForBulkResult = (result) => {
  const isListing = result.layer === 'listing';
  if (result.kind === 'variant') {
    return isListing ? 'listingVariant' : 'masterVariant';
  }
  return isListing ? 'listing' : 'master';
};

export const mergeBulkUpdateResults = (results = []) => (
  results.reduce((acc, result) => ({
    total: acc.total + (result.total || 0),
    updated: acc.updated + (result.updated || 0),
    skipped: acc.skipped.concat(
      (result.skipped || []).map((item) => ({
        ...item,
        id: item.id || item._id,
        entityType: entityTypeForBulkResult(result),
      })),
    ),
    failed: acc.failed.concat(
      (result.failed || []).map((item) => ({
        ...item,
        id: item.id || item._id,
        entityType: entityTypeForBulkResult(result),
      })),
    ),
  }), {
    total: 0,
    updated: 0,
    skipped: [],
    failed: [],
  })
);
